import React, { useEffect } from 'react';
import { Box, Typography, TableRow, TableCell, IconButton } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate, useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from 'store/store';
import { fetchUsersByLottery, deleteUser } from 'store/actions/userActions';
import { StyledTable } from 'components/StyledTable/StyledTable';
import { useTableSort, type SortableColumn } from 'components/StyledTable/useTableSort';
import { DeleteDialog } from 'components/dialog/DeleteDialog';
import RedButton from 'Styled/RedButton';
import { isFulfilled } from '@reduxjs/toolkit';
import { PageHeader } from 'components/Layout/PageHeader';
import { CustomMenu, type CustomMenuRow } from 'components/CustomMenu/CustomMenu';
import { usePermissions } from 'hooks/usePermissions';
import { isSuperAdminUser } from 'utility/userPermissions';
import type { IUser } from 'types/IUser';

const roleNames = (user: IUser) =>
  (user.userAccountPermissions ?? [])
    .map((entry) => entry.permission?.name)
    .filter(Boolean)
    .join(', ') || '-';

const columns: SortableColumn<IUser>[] = [
  { name: 'Benutzername', width: '35%', sortBy: (user) => user.username },
  { name: 'E-Mail', width: '35%', sortBy: (user) => user.email },
  { name: 'Rolle', width: '30%', sortBy: (user) => roleNames(user) },
  { name: 'Aktionen', width: '1%', sx: { textAlign: 'right' } },
];

export const UserListPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { lotteryId } = useParams<{ lotteryId: string }>();
  const { users, loading } = useAppSelector((state) => state.users);
  const { rows: sortedUsers, columns: tableColumns, sort } = useTableSort(columns, users);
  const currentUser = useAppSelector((state) => state.auth.user);
  const { isSuperAdmin } = usePermissions();
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState<number | null>(null);
  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);
  const [menuUser, setMenuUser] = React.useState<IUser | null>(null);

  useEffect(() => {
    if (lotteryId) {
      dispatch(fetchUsersByLottery(Number(lotteryId)));
    }
  }, [dispatch, lotteryId]);


  const canManage = (user: IUser) => isSuperAdmin() || !isSuperAdminUser(user);

  const buildMenuItems = (): CustomMenuRow[] => {
    if (!menuUser || !canManage(menuUser)) return [];

    const rows: CustomMenuRow[] = [{ name: 'Bearbeiten', onClick: () => navigate(`/lottery/${lotteryId}/users/${menuUser.id}/edit`) }];

    if (Number(currentUser?.id) !== Number(menuUser.id)) {
      rows.push({
        name: 'Löschen',
        onClick: () => {
          setSelectedId(menuUser.id);
          setDeleteDialogOpen(true);
        },
      });
    }

    return rows;
  };

  const handleDelete = async () => {
    if (selectedId) {
      const result = await dispatch(deleteUser(selectedId));
      if (isFulfilled(result)) {
        setDeleteDialogOpen(false);
        setSelectedId(null);
      }
    }
  };

  return (
    <Box>
      <PageHeader
        title="Benutzerverwaltung"
        actions={
          <RedButton startIcon={<AddIcon />} onClick={() => navigate(`/lottery/${lotteryId}/users/create`)}>
            Neuer Benutzer
          </RedButton>
        }
      />

      {!loading && users.length === 0 && (
        <Typography variant="body1" sx={{ color: '#969696' }}>
          Keine Benutzer vorhanden.
        </Typography>
      )}

      {users.length > 0 && (
        <StyledTable columns={tableColumns} sort={sort}>
          {sortedUsers.map((user, index) => (
            <TableRow key={user.id} className={`psf-styled-table__body-row ${index === sortedUsers.length - 1 ? 'psf-styled-table__body-last-row' : ''}`}>
              <TableCell>{user.username}</TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell>{roleNames(user)}</TableCell>
              <TableCell>
                <Box className="psf-styled-table__body-cell-action-container">
                  {canManage(user) && (
                    <IconButton
                      className="psf-styled-table__body-cell-action-button"
                      aria-label="Aktionen"
                      onClick={(event) => {
                        setMenuAnchor(event.currentTarget);
                        setMenuUser(user);
                      }}
                    >
                      <MoreVertIcon className="psf-styled-table__body-cell-action-button-icon" />
                    </IconButton>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </StyledTable>
      )}

      <CustomMenu
        anchorEl={menuAnchor}
        handleMenuClose={() => {
          setMenuAnchor(null);
          setMenuUser(null);
        }}
        withDivider
        isArrowRight
        items={buildMenuItems()}
      />

      <DeleteDialog open={deleteDialogOpen} title="Benutzer löschen" onDelete={handleDelete} onCancel={() => setDeleteDialogOpen(false)}>
        <Typography className="psf-dialog-delete__text">Möchten Sie diesen Benutzer wirklich löschen?</Typography>
      </DeleteDialog>
    </Box>
  );
};
